import { useEffect, useState } from 'react';

import * as appHelpers from '@src/services/helpers/appHelpers';
import { setPopup, openCollectOverlay } from '@src/state/popupAtoms';
import { CollectionView } from 'common';
import { EPopup } from '@src/types/common';

import { AddToCollection } from '@src/types/Popups';
import { useQueryCollections } from '@src/queries/collections/useQueryCollections';
import { useMutationCollectionAddMedia } from '@src/queries/collections/useMutationCollection';
import { useQueryMedia } from '@src/queries/media/useQueryMedia';
import { getLangError } from '@src/fragments/Alerting/AlertSnackbar';
import TextContent from '@src/components/Media/TextContent';
import { classes } from 'typestyle';
import { style } from 'typestyle';
import { MediaType } from '@adalong/schemas';
import { Loader } from '@src/components/Loader';
import { SearchAndSelectBar } from '@src/components/SearchAndSelectBar';
import { useAddAlert } from '@src/fragments/Alerting/alertAtom';
import { ActionButton } from '@src/components/ActionButton';
import { PerfectScrollbar } from '../../../../../../components/PerfectScrollbar';
import { MediaImage } from '../../../../../../components/MediaImage';
import { CollectionThumb } from '../../../../../Collection/components/CollectionThumb';
import {
  collectFrameClass,
  collectFrameCompactClass,
  collectFrameLoadingClass,
  collectFrameLeftClass,
  collectFrameRightClass,
  collectFrameTitleClass,
  collectFrameRemoveClass,
  loaderCenteredClass as sharedLoaderCenteredClass,
} from '../sharedStyles';
import { ADALONG_COLORS } from '@src/styles';

interface IProps {
  media?: AddToCollection
}

type CollectionMembershipFilter = 'all' | 'in' | 'out';

export function AddTo(props: IProps) {
  const { media = [] } = props;
  const addAlert = useAddAlert();

  const { data: collections = [], isLoading: isLoadingCollections } = useQueryCollections();
  const {
    addMediaInBatches,
    isPending: isAddingMedia,
    reset,
  } = useMutationCollectionAddMedia();
  const { data: fullMedia } = useQueryMedia(media[0]?._id || '', {
    enabled: media.length === 1 && !!media[0]?._id,
  });
  const [filter, setFilter] = useState<string>('');
  const [membershipFilter, setMembershipFilter] = useState<CollectionMembershipFilter>('all');
  const mediaCollectionIds = fullMedia?.collections ?? media[0]?.collections ?? [];
  const normalizedFilter = filter.trim().toLowerCase();
  const editableCollections = collections.filter((c) => c.i_can_edit && !c.locked);
  const hasEditableCollections = editableCollections.length > 0;
  const textFilteredCollections = normalizedFilter.length
    ? editableCollections.filter((collection) => collection.name.toLowerCase().includes(normalizedFilter))
    : editableCollections;

  const isMediaInCollection = (collection: CollectionView) => {
    return mediaCollectionIds.includes(collection._id);
  };

  const onClickCollection = async (collection: CollectionView) => {
    if (!props.media || collection.locked) {
      return;
    }

    if (isMediaInCollection(collection)) {
      appHelpers.addAlert({
        text: 'collect.collectoverlay.addto.incollection',
        type: 'warning',
      });
      return;
    }

    try {
      await addMediaInBatches(collection._id, props.media?.map((m) => m._id));
      appHelpers.addAlert({
        text: 'collect.collectoverlay.success',
        type: 'success',
      });

      setPopup(EPopup.collectOverlay, false);
    } catch (error) {
      if (error instanceof Error) {
        addAlert({
          text: getLangError(error.message),
          close: reset,
        });
      } else {
        addAlert({
          text: 'An unknown error occurred',
          close: reset,
        });
      }
    }
  };

  const collectionsICanEdit = textFilteredCollections.filter((collection) => {
    if (membershipFilter === 'in') {
      return isMediaInCollection(collection);
    }

    if (membershipFilter === 'out') {
      return !isMediaInCollection(collection);
    }

    return true;
  });

  const loading = isLoadingCollections || isAddingMedia;
  const loadingClassName = loading ? collectFrameLoadingClass : '';
  const hasSingleMediaPreview = media.length === 1 && media[0]?.type !== MediaType.Text;

  useEffect(() => {
    setFilter('');
    setMembershipFilter('all');
  }, [media]);

  return (
    <>
      {loading ? <div className={sharedLoaderCenteredClass}><Loader size={40} /></div> : null}
      <div className={classes(collectFrameClass, !hasSingleMediaPreview ? collectFrameCompactClass : '', loadingClassName)}>
        {hasSingleMediaPreview
                    ? (
                    <div className={collectFrameLeftClass}>
                      <MediaImage src={media[0].cdn_image} style={{ width: '100%', height: 'auto' }} />
                    </div>
                    ) : null }
        {media[0]?.type === MediaType.Text
                    ? <TextContent caption={media[0].caption} />
                    : null}
        <div className={collectFrameRightClass}>
          <div className={collectFrameTitleClass}>
            {window.T('collect.collectoverlay.addto.title')}
            <div className={collectFrameRemoveClass} onClick={() => setPopup(EPopup.collectOverlay, false)}>
              <i className="material-icons notranslate">close</i>
            </div>
          </div>
          <div className={newClass}>
            <ActionButton
              variant='blue'
              className={newButtonClass}
              minWidth='100%'
              onAction={() => openCollectOverlay('new', media)}
            >
              {window.T('collect.collectoverlay.addto.new').replace(/^\+\s*/, '')}
            </ActionButton>
          </div>
          <div className={searchBar}>
            <SearchAndSelectBar
              inputValue={filter}
              handlers={{
                onText: (val) => setFilter(val),
                onCloseBar: () => setFilter(''),
              }}
            />
          </div>
          {hasEditableCollections ? (
            <div className={pillsRowClass}>
              <button
                type='button'
                className={classes(filterPillClass, membershipFilter === 'all' ? filterPillActiveClass : '')}
                onClick={() => setMembershipFilter('all')}
              >
                All
              </button>
              <button
                type='button'
                className={classes(filterPillClass, membershipFilter === 'in' ? filterPillActiveClass : '')}
                onClick={() => setMembershipFilter('in')}
              >
                In collection
              </button>
              <button
                type='button'
                className={classes(filterPillClass, membershipFilter === 'out' ? filterPillActiveClass : '')}
                onClick={() => setMembershipFilter('out')}
              >
                Not in collection
              </button>
            </div>
          ) : null}
          <PerfectScrollbar
            className={collectionsClass}
            options={{
              wheelSpeed: 0.3,
              minScrollbarLength: 30,
              suppressScrollX: true,
            }}
          >
            
            <div className={collectionsGridClass}>
              {collectionsICanEdit.map((collection) => (
                <CollectionThumb
                  className={collectionThumbClass}
                  collection={collection}
                  key={collection._id}
                  inCollectionMark={isMediaInCollection(collection)}
                  onClick={() => {
                    void onClickCollection(collection);
                  }}
                  backgroudImageUrl={collection.preview_media_url ?? undefined}
                />
              ))}
            </div>
          </PerfectScrollbar>
        </div>
      </div>
    </>

  );
}



const collectionsClass = style({
  flex: '1 1 300px',
  minHeight: 300,
  maxHeight: '100%',
});
const collectionsGridClass = style({ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, gridAutoRows: 130 });
const newClass = style({ padding: '16px 0 10px', width: '100%' });
const newButtonClass = style({ width: '100%' });
const searchBar = style({ width: '100%', marginBottom: 10 });
const pillsRowClass = style({ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' });
const filterPillClass = style({
  border: '1px solid #d8dee6',
  background: '#fff',
  color: '#2f3f53',
  borderRadius: 999,
  padding: '2px 8px',
  fontSize: 12,
  cursor: 'pointer',
});
const filterPillActiveClass = style({
  borderColor: ADALONG_COLORS.FLASH_BLUE,
  background: ADALONG_COLORS.FLASH_BLUE,
  color: ADALONG_COLORS.LIGHT_WHITE,
  fontWeight: 600,
});
const collectionThumbClass = style({ width: '100%' });

export default AddTo;
